import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  makeStyles,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Chip,
} from '@material-ui/core';
import { loader } from '@monaco-editor/react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useGenaiBuilderApi, Agent } from './useGenaiBuilderApi';

// Use the locally bundled monaco-editor instead of loading from CDN,
// which would be blocked by the Backstage Content Security Policy.
import * as monaco from 'monaco-editor';
loader.config({ monaco });

const MonacoEditor = lazy(() =>
  import('@monaco-editor/react').then((m) => ({ default: m.default }))
);

const DiffEditor = lazy(() =>
  import('@monaco-editor/react').then((m) => ({ default: m.DiffEditor }))
);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  editorLabel: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  editorControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  editorWrapper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  slugWarning: {
    color: theme.palette.warning.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  tokenCount: {
    color: theme.palette.text.hint,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
    textAlign: 'right',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
  },
  folderSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
  },
  folderLabel: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    fontSize: '0.85rem',
  },
  folderChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  folderChip: {
    cursor: 'pointer',
  },
}));

interface AgentFormProps {
  agent?: Agent;
  onSaved: (agent: Agent) => void;
  onCancel: () => void;
}

function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

export function AgentForm({ agent, onSaved, onCancel }: AgentFormProps) {
  const classes = useStyles();
  const api = useGenaiBuilderApi();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [slug, setSlug] = useState(agent?.slug ?? '');
  const [name, setName] = useState(agent?.name ?? '');
  const [description, setDescription] = useState(agent?.description ?? '');
  const [model, setModel] = useState(agent?.model ?? '');
  const [prompt, setPrompt] = useState(agent?.prompt ?? '');
  const [savedPrompt, setSavedPrompt] = useState(agent?.prompt ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configSlugs, setConfigSlugs] = useState<string[]>([]);
  const [slugWarning, setSlugWarning] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);

  // Folder-based RAG scoping
  const initialFolders = agent?.rag_folders
    ? agent.rag_folders.split(',').map(f => f.trim()).filter(f => f.length > 0)
    : [];
  const [selectedFolders, setSelectedFolders] = useState<string[]>(initialFolders);
  const [availableFolders, setAvailableFolders] = useState<string[]>([]);

  // RAG diagnostics
  const [ragDiag, setRagDiag] = useState<{ pgvectorEnabled: boolean; chunks: number; pendingJobs: number; failedJobs: number; failedJobDetails?: Array<{ id: string; error_message: string | null }> } | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('aws-genai');
        const resp = await fetchApi.fetch(`${base}/rag-diagnostics`);
        if (resp.ok && !cancelled) setRagDiag(await resp.json());
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  const isEdit = Boolean(agent);
  const tokenCount = estimateTokens(prompt);

  const { getConfigSlugs, listDocuments } = api;

  useEffect(() => {
    if (!isEdit) {
      getConfigSlugs().then(slugs => setConfigSlugs(slugs)).catch(() => setConfigSlugs([]));
    }
  }, [isEdit, getConfigSlugs]);

  // Load available folders from documents
  useEffect(() => {
    listDocuments().then(docs => {
      const folders = Array.from(
        new Set(docs.map(d => d.folder).filter((f): f is string => !!f))
      ).sort();
      setAvailableFolders(folders);
    }).catch(() => setAvailableFolders([]));
  }, [listDocuments]);

  const toggleFolder = (folder: string) => {
    setSelectedFolders(prev =>
      prev.includes(folder) ? prev.filter(f => f !== folder) : [...prev, folder]
    );
  };

  const handleSlugBlur = () => {
    if (slug && configSlugs.includes(slug)) {
      setSlugWarning('This slug overrides an existing config-defined agent');
    } else {
      setSlugWarning(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const ragFolders = selectedFolders.length > 0 ? selectedFolders.join(',') : null;
      let saved: Agent;
      if (isEdit && agent) {
        saved = await api.updateAgent(agent.id, {
          name,
          prompt,
          description,
          model: model || null,
          rag_folders: ragFolders,
        });
      } else {
        saved = await api.createAgent({
          slug,
          name,
          prompt,
          description,
          model: model || null,
          rag_folders: ragFolders,
        });
      }
      setSavedPrompt(saved.prompt);
      onSaved(saved);
    } catch (err: any) {
      setError(err.message ?? 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={classes.root}>
      {!isEdit && (
        <Box>
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            onBlur={handleSlugBlur}
            required
            disabled={saving}
            helperText="Unique identifier used in URLs and config. Lowercase, hyphens only."
            fullWidth
            variant="outlined"
            size="small"
          />
          {slugWarning && (
            <Typography className={classes.slugWarning}>
              ⚠ {slugWarning}
            </Typography>
          )}
        </Box>
      )}

      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        disabled={saving}
        fullWidth
        variant="outlined"
        size="small"
      />

      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        disabled={saving}
        fullWidth
        variant="outlined"
        size="small"
        multiline
        rows={2}
      />

      <TextField
        label="Bedrock Model ID"
        value={model}
        onChange={(e) => setModel(e.target.value)}
        disabled={saving}
        fullWidth
        variant="outlined"
        size="small"
        placeholder="e.g. arn:aws:bedrock:eu-central-1::foundation-model/..."
        helperText="Leave empty to use the default model from BEDROCK_MODEL_ID env var."
      />

      {availableFolders.length > 0 && (
        <Box className={classes.folderSection}>
          <Typography className={classes.folderLabel}>
            RAG Knowledge Scope (optional)
          </Typography>
          <Typography variant="caption" style={{ display: 'block', marginBottom: 8 }}>
            {selectedFolders.length === 0
              ? 'Mode: Individual document associations (from the Documents tab). Select folders below to switch to folder-based scoping.'
              : `Mode: Folder-based scoping. Agent will search ALL documents in: ${selectedFolders.join(', ')}`}
          </Typography>
          {ragDiag && (
            <Box style={{ marginBottom: 12 }}>
              <Typography
                variant="caption"
                style={{
                  display: 'block',
                  color: !ragDiag.pgvectorEnabled
                    ? '#d32f2f'
                    : ragDiag.failedJobs > 0 && ragDiag.chunks === 0
                    ? '#d32f2f'
                    : ragDiag.pendingJobs > 0 || ragDiag.chunks === 0
                    ? '#f57c00'
                    : '#388e3c',
                }}
              >
                {!ragDiag.pgvectorEnabled
                  ? 'RAG unavailable: pgvector extension not enabled on this PostgreSQL instance.'
                  : ragDiag.failedJobs > 0 && ragDiag.chunks === 0
                  ? `Ingestion failed: ${ragDiag.failedJobs} job(s) failed. Check Bedrock permissions or see error below.`
                  : ragDiag.pendingJobs > 0
                  ? `Indexing in progress: ${ragDiag.pendingJobs} job(s) pending.`
                  : ragDiag.chunks === 0
                  ? 'No indexed chunks found. Upload documents and wait for ingestion to complete.'
                  : `${ragDiag.chunks} indexed chunks ready.`}
              </Typography>
              {ragDiag.failedJobDetails && ragDiag.failedJobDetails.length > 0 && (
                <Typography
                  variant="caption"
                  style={{ display: 'block', color: '#d32f2f', fontFamily: 'monospace', marginTop: 4 }}
                >
                  Last error: {ragDiag.failedJobDetails[0].error_message ?? 'Unknown error'}
                </Typography>
              )}
            </Box>
          )}
          <Box className={classes.folderChips}>
            {availableFolders.map((folder) => (
              <Chip
                key={folder}
                label={folder}
                onClick={() => toggleFolder(folder)}
                color={selectedFolders.includes(folder) ? 'primary' : 'default'}
                variant={selectedFolders.includes(folder) ? 'default' : 'outlined'}
                className={classes.folderChip}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box>
        <Box className={classes.editorControls}>
          <Typography variant="caption" className={classes.editorLabel}>
            System Prompt *
          </Typography>
          {isEdit && (
            <Button
              size="small"
              onClick={() => setShowDiff(!showDiff)}
              variant="text"
            >
              {showDiff ? 'Hide diff' : 'Show diff'}
            </Button>
          )}
        </Box>
        <Box className={classes.editorWrapper}>
          <Suspense fallback={<CircularProgress size={24} />}>
            {showDiff && isEdit ? (
              <DiffEditor
                height="400px"
                language="markdown"
                original={savedPrompt}
                modified={prompt}
                options={{
                  wordWrap: 'on',
                  lineNumbers: 'off',
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  readOnly: saving,
                }}
              />
            ) : (
              <MonacoEditor
                height="400px"
                defaultLanguage="markdown"
                value={prompt}
                onChange={(val) => setPrompt(val ?? '')}
                options={{
                  wordWrap: 'on',
                  lineNumbers: 'off',
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  readOnly: saving,
                }}
              />
            )}
          </Suspense>
        </Box>
        <Typography className={classes.tokenCount}>
          ~{tokenCount.toLocaleString()} tokens
        </Typography>
      </Box>

      {error && (
        <Typography className={classes.error} variant="body2">
          {error}
        </Typography>
      )}

      <Box className={classes.actions}>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={saving || !name || !prompt || (!isEdit && !slug)}
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : isEdit ? (
            'Save Changes'
          ) : (
            'Create Agent'
          )}
        </Button>
      </Box>
    </form>
  );
}
